import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
    Alert,
    Autocomplete,
    Box,
    Grid,
    IconButton,
    InputAdornment,
    Snackbar,
} from '@mui/material';
import { Button } from '@uhc-tempo/components';
import { Paper, TextField } from '@uhc-tempo/components/node_modules/@mui/material';
import '@uhc-tempo/components/tempo.css';
import { debounce } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getPrice } from '../../../API/PriceService';
import * as ProviderSearchService from '../../../API/ProviderSearchService';
import {
    ERROR_ROUTE,
    ESTIMATES_ROUTE,
    SINGLE_ESTIMATE_ROUTE,
    VISION_MATERIALS,
    NO_BENEFITS,
} from '../../../constants';
import { FormContext } from '../../../context/Form.context';
import styles from '../../../styles/PageStyles.js';
import theme from '../../../styles/PageTheme';
import useDetectMobile from '../../../utils/customHooks/useDetectMobile';
import MapboxLogic from '../../../utils/mapboxLogic';
import './Search.page.css';
import './SearchBar.component.css';
import {
    getCarePathIdAndCategoryCode,
    checkIndemnityCoverages,
    shouldNotPerformProviderSearch,
    checkForRevNoEstimateServiceType,
    checkIfMemberHasVisionCoverageType,
} from '../../../utils/SearchPageUtil';
import LocationSearchErrorCard from './LocationSearchErrorCard.component';
import ServiceDisclaimerBanner from './ServiceDisclaimerBanner.component';
import ProviderNoOptionsDropdown from './ProviderNoOptionsDropdown.component';
import ServiceSearchInput from './ServiceSearchInput.component';
import { mapProviders } from '../../../utils/ProviderUtil';
import { getVisionPrice } from '../../../API/VisionPriceService.js';

export default function SearchBar(props) {
    const classes = styles(theme);
    const {
        formValues,
        setAllProvidersPool,
        setEstimateResponse,
        setSingleEstimateResponse,
        handleChangeCustom,
        memberDetails,
        sourceSystem,
        getIncrementedCorId,
        incrementSearchCount,
        planBenefits,
        setPlanBenefits,
        setEstimateParameters,
        setVisionEstimateResponse,
    } = useContext(FormContext);
    const isIndemnity = checkIndemnityCoverages(memberDetails);
    const umrIndemnity = checkIndemnityCoverages(memberDetails, true);
    const history = useHistory();
    const { locationOfService, radius, selectedService, selectedProvider } = formValues;
    const { setLoading, setProvider, setCurrentSelectedServiceForDisplay } = props;

    /**
     * Location & Radius Options
     */
    const [openLocatonPopper, setOpenLocationPopper] = useState(false);
    const closeLocationPopper = () => setOpenLocationPopper(false);
    const openLocationPopper = () => setOpenLocationPopper(true);
    const [emptyLocation, setEmptyLocation] = useState(false);
    const locationInputRef = useRef();
    const [locationOptions, setLocationOptions] = useState([]);

    const radiusOptions = [
        { label: '1mi', value: 1 },
        { label: '2mi', value: 2 },
        { label: '5mi', value: 5 },
        { label: '10mi', value: 10 },
        { label: '20mi', value: 20 },
        { label: '30mi', value: 30 },
        { label: '50mi', value: 50 },
    ];
    const [serviceFocus, setServiceFocus] = useState(false);
    const [locationFocus, setLocationFocus] = useState(false);
    const [searchButtonDisabled, setSearchButtonDisabled] = useState(false);
    const [providerSearchOpen, setProviderSearchOpen] = useState(false);
    const [navigateWithKey, setNavigateWithKey] = useState(false);
    const [providerSearchLoading, setProviderSearchLoading] = useState(false);
    const [innNoNetworkDisclaimerOpen, setInnNoNetworkDisclaimer] = useState(false);
    const [noCoverageDisclaimerOpen, setNoCoverageDisclaimer] = useState(false);

    const [innNetworkGacFlow, setInnNetworkGacFlow] = useState(false);
    const [serviceTypeDisclaimer, setServiceTypeDisclaimer] = useState(false);
    const [disableProviderDropdown, setDisableProviderDropdown] = useState(false);
    const [dentalPolicyDisclaimer, setDentalPolicyDisclaimer] = useState(false);

    const ref = useRef(null);
    const isDesktopView = useDetectMobile(ref);

    useEffect(() => {
        incrementSearchCount();
    }, []);

    /**
     * Provider Data methods & states
     */
    const [filteredProviderData, setProviderData] = useState([]);

    /**
     * Snackbar state variables
     */
    const [providerSearchFailureSnackBar, setProviderSearchFailureSnackBar] = useState(false);

    const handleProviderSearchFailureSnackBarClose = () => {
        setProviderSearchFailureSnackBar(false);
    };

    /**
     * Checks condition when there is PNI but no providers are found and returned,to generate Gac only INN estimates and disclaimer in provider dropdown.
     * @param {Array} filteredProviderData -  filteredProviderData state variable updated with response from cptt provider search
     * @param {Boolean} hasPlanNetworkIndicators -  boolean determining if member has network indicators coming from cptt provider search response
     * @param {String} providerName - string containing provider name
     */
    const checkNetworkIndicatorAndProviders = (
        filteredProviderData,
        hasPlanNetworkIndicators,
        providerName
    ) => {
        if (
            filteredProviderData.length == 0 &&
            hasPlanNetworkIndicators &&
            providerName.length == 0 //value checked to prevent case where member with providers returned initially returns zero providers when searching for a specific provider.
        ) {
            setProviderSearchOpen(false);
            setInnNetworkGacFlow(true);
            service?.current?.focus();
        }
    };

    /**x
     * fetches provider data via the PES Facade via CPTT Prov Search
     * SIDE EFFECT(S): provider data is set to a state var in a form that is conducive to rendering on the frontend
     * @param {String} providerName - a user inputted string searching for a provider's name,
     *      can be empty (which searches for all providers who can perform the service within the radius
     *      near the location the user searches for)
     * @returns provider data relevant to the search performed, if available
     */

    const fetchProviderData = async (providerName = '') => {
        // if no selected service in the case when we start a new search, then we want to return until user selects a service
        const [carePathStepId, carePathCategoryType, providerType] = getCarePathIdAndCategoryCode(
            selectedService.value
        );

        if (!carePathStepId || !carePathCategoryType || !providerType) {
            setProviderData([]);
            return [];
        }

        const memberDetailsForProviderSearch =
            ProviderSearchService.getMemberDetailsForProviderSearch(
                memberDetails,
                carePathCategoryType
            );

        const cpttProvSearchPayload = ProviderSearchService.generatePractOrgQueryParams(
            memberDetailsForProviderSearch,
            selectedService,
            locationOfService,
            radius,
            providerName,
            carePathStepId,
            carePathCategoryType,
            providerType
        );

        try {
            const { providerSearchResponse, genericErrorResponse } =
                await ProviderSearchService.postProviderSearch(
                    process.env.CPTT_PROVIDER_SEARCH,
                    cpttProvSearchPayload,
                    getIncrementedCorId()
                );

            const checkForNoCoverageDisclaimer = providerSearchResponse
                ? providerSearchResponse.noCoverageDisclaimerFlag
                : genericErrorResponse?.providerSearchResponse?.noCoverageDisclaimerFlag;

            const checkForInnNoNetworkDisclaimer = providerSearchResponse
                ? providerSearchResponse.innNoNetworkDisclaimerFlag
                : genericErrorResponse?.providerSearchResponse?.innNoNetworkDisclaimerFlag;

            setNoCoverageDisclaimer(checkForNoCoverageDisclaimer);
            setInnNoNetworkDisclaimer(checkForInnNoNetworkDisclaimer);
            setDisableProviderDropdown(checkForNoCoverageDisclaimer);

            if (providerSearchResponse) {
                const providersById = providerSearchResponse.providersById;
                const sortedData = mapProviders(Object.values(providersById)).sort((a, b) => {
                    return a.label.localeCompare(b.label);
                });

                setProviderData([...sortedData]);

                handleChangeCustom({ results: [...sortedData] }, 'selectedProviderForEstimates');

                // we want to make sure at least 1 provider is returned
                if (Object.keys(providersById).length < 1) {
                    setSearchButtonDisabled(true);
                } else {
                    setSearchButtonDisabled(false);
                }
                setProviderSearchLoading(false);

                checkNetworkIndicatorAndProviders(
                    Object.keys(filteredProviderData),
                    providerSearchResponse.hasPlanNetworkIndicators,
                    providerName
                );
                return [...sortedData];
            } else {
                setProviderSearchOpen(true);
                setInnNetworkGacFlow(true);
                providerInputRef?.current?.focus();
            }
            setProviderData([]);
            handleChangeCustom({ results: [] }, 'selectedProviderForEstimates');

            setSearchButtonDisabled(true);
            setProviderSearchLoading(false);
            return [];
        } catch (error) {
            console.error('Error communicating with provider search', error);
            setProviderData([]);
            handleChangeCustom({ results: [] }, 'selectedProviderForEstimates');
            setInnNoNetworkDisclaimer(
                isIndemnity[selectedService?.value?.fullCarePath?.coverageTypeCode]
            );
            setSearchButtonDisabled(true);
            setProviderSearchLoading(false);

            return [];
        }
    };

    useEffect(() => {
        if (selectedService.value) {
            providerInputRef.current.focus();
        }
    }, [selectedProvider.value]);

    const checkForCoveredVisionNoBenefitsSelection = () => {
        const serviceTypes = selectedService?.value?.fullCarePath?.serviceTypes;
        if (
            serviceTypes?.includes(VISION_MATERIALS) &&
            checkIfMemberHasVisionCoverageType(memberDetails)
        ) {
            if (serviceTypes?.includes(NO_BENEFITS)) {
                setSearchButtonDisabled(true);
            } else setSearchButtonDisabled(false);
        }
    };
    useEffect(() => {
        if (
            locationOfService.value != null &&
            radius.value != null &&
            selectedService.value != null
        ) {
            const procedureCode = selectedService?.value?.lineItem?.billingCode?.code;
            // Only attempt to set Provider Data if we have a CPT code and
            // were able to retrieve information from allProviderData for the CPT code.
            // make sure not part of certain serviceType since those dislcaimers we do not want to search for providers
            if (
                procedureCode &&
                !shouldNotPerformProviderSearch(
                    selectedService?.value?.fullCarePath?.serviceTypes,
                    selectedService?.value?.fullCarePath?.coverageTypeCode,
                    memberDetails,
                    selectedService?.value
                )
            ) {
                setProviderSearchLoading(true);
                fetchProviderData();
            } else {
                setTimeout(checkForCoveredVisionNoBenefitsSelection, 100); // Using timeout to override the setSearchButtonDisabled(true) in case of valid vision selection
                setProviderData([]);
            }
        } else {
            if (serviceFocus) {
                service.current.focus(); //focus after service closure here, but not on page load
                setServiceFocus(false);
            } else if (locationFocus) {
                locationInputRef.current.focus();
                setLocationFocus(false);
            }
            setProviderData([]);
        }
        setSearchButtonDisabled(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationOfService, radius.value, selectedService.value]);

    useEffect(() => {
        if (
            locationOfService.value != null &&
            radius.value != null &&
            selectedService.value != null
        ) {
            if (
                selectedService?.value?.hasBillingCodeGroup &&
                !checkForRevNoEstimateServiceType(
                    selectedService?.value?.fullCarePath?.serviceTypes
                )
            ) {
                setProviderSearchOpen(false);
                setSearchButtonDisabled(false);
            }
        }
    }, [locationOfService, radius.value, selectedService.value]);

    const [customPopper, setCustomPopper] = useState(false);
    const service = useRef();
    const providerInputRef = useRef();
    const [message, setMessage] = useState(false);

    /**
     * Set location options passed to Autocomplete using MapBox API response, returning an array of feature objects
     * @param {any} inputAddress value passed from Autocomplete Input field [can be object or string]
     */
    const handleAddressInputChange = async (inputAddress) => {
        if (inputAddress) {
            const inputAddrStr =
                typeof inputAddress === 'object' ? inputAddress.place_name : inputAddress;
            try {
                const response = await MapboxLogic.getMapboxAddressResponse(inputAddrStr);
                const mapboxLocationOptions = await MapboxLogic.guaranteePostalCodes(
                    response.data.features
                );
                setLocationOptions(mapboxLocationOptions);
            } catch (error) {
                setLocationOptions([]);
            }
        } else {
            setLocationOptions([]);
        }
    };

    const debouncedLocationChange = debounce(handleAddressInputChange, 250);
    const debounceProviderSearch = debounce(fetchProviderData, 1000);

    /**
     *
     * @param {Boolean} allProviders
     * @param {Boolean} oon - true if requesting out of network estimate
     * @param {Boolaean} gacOnly - true if
     * @returns
     */
    const handleSubmit = async ({ allProviders = false, oon = false, gacOnly = false } = {}) => {
        setEstimateParameters({ allProviders, oon, gacOnly });

        if (!selectedService.value) {
            service.current.focus();
            setCustomPopper(true);
            setMessage(true);
            return false;
        }
        if (!locationOfService.value) {
            locationInputRef.current.focus();
            setEmptyLocation(true);
            openLocationPopper();
            return false;
        }
        setLoading(true);

        await checkAndFetchFacilityProviderData(gacOnly, oon);

        if (selectedProvider?.value || allProviders || oon || gacOnly) {
            if (oon) {
                getSingleEstimate([], oon);
            } else if (gacOnly) {
                getSingleEstimate([], oon, gacOnly);
            } else {
                fetchProviderData().then((res) => {
                    getEstimates(res);
                });
            }
        } else if (
            selectedService?.value?.fullCarePath?.serviceTypes?.includes(VISION_MATERIALS) &&
            checkIfMemberHasVisionCoverageType(memberDetails)
        ) {
            getVisionEstimates();
        } else {
            getEstimates(filteredProviderData);
        }
    };

    const checkAndFetchFacilityProviderData = async (gacOnly, oon) => {
        const selectedServiceForFacilityCharge = selectedService.value?.facilityChargeInfo;
        const [_carePathStepId, carePathCategoryType, _providerType] = getCarePathIdAndCategoryCode(
            selectedService.value
        );
        const memberDetailsForProviderSearch =
            ProviderSearchService.getMemberDetailsForProviderSearch(
                memberDetails,
                carePathCategoryType
            );

        const backupProvidersNoFacilities = filteredProviderData.map(
            (prov) => (prov.facilitiesData = [])
        );

        if (
            selectedServiceForFacilityCharge &&
            !gacOnly &&
            !oon &&
            memberDetailsForProviderSearch.sourceCode != 'UM' &&
            memberDetailsForProviderSearch.sourceCode != 'PH'
        ) {
            const [facilityCarePathStepId, facilityCarePathCategoryType, facilityProviderType] =
                getCarePathIdAndCategoryCode(selectedServiceForFacilityCharge);
            const practOrgQueryParams = ProviderSearchService.generatePractOrgQueryParams(
                memberDetailsForProviderSearch,
                { value: selectedServiceForFacilityCharge },
                locationOfService,
                radius,
                '',
                facilityCarePathStepId,
                facilityCarePathCategoryType,
                facilityProviderType
            );

            const chosenProviders =
                selectedProvider && selectedProvider.value
                    ? [selectedProvider.value]
                    : filteredProviderData;

            const payloadForHospitalAffiliationSearch =
                ProviderSearchService.buildHospitalAffiliationRequest(
                    practOrgQueryParams,
                    chosenProviders
                );

            const affiliatedProvidersResp =
                await ProviderSearchService.postAffiliatedHospitalsSearch(
                    process.env.PROVIDER_AFFILIATIONS_SEARCH,
                    payloadForHospitalAffiliationSearch,
                    getIncrementedCorId()
                );

            if (affiliatedProvidersResp) {
                const updatedProvidersAndFacilities =
                    ProviderSearchService.mapFacilitiesToPhysicians(
                        chosenProviders,
                        affiliatedProvidersResp
                    );
                setProviderData([...updatedProvidersAndFacilities]);
                handleChangeCustom(
                    { results: [...updatedProvidersAndFacilities] },
                    'selectedProviderForEstimates'
                );
                if (selectedProvider && selectedProvider.value) {
                    handleChangeCustom(updatedProvidersAndFacilities[0], 'selectedProvider');
                }
            } else {
                console.warn('No hospital affiliations were retrieved for physicians');
                setProviderData([...backupProvidersNoFacilities]);
            }
        } else {
            setProviderData([...backupProvidersNoFacilities]);
        }
    };

    const getEstimates = async (providerData = filteredProviderData) => {
        const planBenefitsResponse = await getBenefitsResponseValue(planBenefits);

        await getPrice(
            formValues,
            sourceSystem,
            providerData,
            false,
            memberDetails,
            planBenefitsResponse,
            getIncrementedCorId()
        )
            .then((res) => {
                if (res instanceof Error) {
                    handleChangeCustom(null, 'selectedService');
                    handleChangeCustom(null, 'selectedProvider');
                    setLoading(false);
                    history.push(ERROR_ROUTE);
                    return;
                }
                setEstimateResponse(res);
                if (selectedProvider && selectedProvider.value) {
                    setProvider(selectedProvider.value);

                    const singleEstimate = res?.scenarios?.filter((scenario) => {
                        if (selectedProvider && selectedProvider.value) {
                            return (
                                scenario.claims[0].procedures[0].provider?.identifiers
                                    ?.nationalProviderId ==
                                selectedProvider.value.nationalProviderId
                            );
                        } else {
                            return false;
                        }
                    });
                    setSingleEstimateResponse(singleEstimate[0]);
                    setAllProvidersPool(filteredProviderData);
                } else {
                    const procedureCode = selectedService?.value?.lineItem?.billingCode?.code;
                    if (procedureCode) {
                        setAllProvidersPool(providerData);
                    } else {
                        setAllProvidersPool([]);
                    }
                }
                setCurrentSelectedServiceForDisplay(selectedService.value);
                if (res?.noInnGACPACRates?.hasNoGACPACRates) {
                    setSingleEstimateResponse(res?.scenarios?.[0]);
                    history.push(SINGLE_ESTIMATE_ROUTE);
                } else if (
                    memberDetails?.sourceCode == 'UM' &&
                    !(res?.scenarios?.length > 0) &&
                    (umrIndemnity?.M || umrIndemnity?.B || umrIndemnity?.D)
                ) {
                    history.push(SINGLE_ESTIMATE_ROUTE); //special routing case for UMR indemnity without returned scenarios
                } else {
                    history.push(ESTIMATES_ROUTE);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error getting price', error);
                setEstimateResponse(null);
                if (selectedProvider && selectedProvider.value) {
                    setProvider(selectedProvider.value);
                }
                setSingleEstimateResponse(null);
                const procedureCode = selectedService?.value?.lineItem?.billingCode?.code;
                if (procedureCode) {
                    setAllProvidersPool(filteredProviderData);
                } else {
                    setAllProvidersPool([]);
                }
                setCurrentSelectedServiceForDisplay(selectedService.value);
                history.push(ESTIMATES_ROUTE);
            });
    };

    const getSingleEstimate = async (providerData, oon = false, gacOnly = false) => {
        const planBenefitsResponse = await getBenefitsResponseValue(planBenefits);
        await getPrice(
            formValues,
            sourceSystem,
            providerData,
            oon,
            memberDetails,
            planBenefitsResponse,
            getIncrementedCorId(),
            gacOnly
        )
            .then((res) => {
                if (res instanceof Error) {
                    handleChangeCustom(null, 'selectedService');
                    handleChangeCustom(null, 'selectedProvider');
                    history.push(ERROR_ROUTE);
                    setLoading(false);
                    return;
                }
                setEstimateResponse(res);
                setSingleEstimateResponse(res?.scenarios?.[0]);
                setCurrentSelectedServiceForDisplay(selectedService?.value);
                history.push(SINGLE_ESTIMATE_ROUTE);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error getting price', error);
                setEstimateResponse(null);
                setSingleEstimateResponse(null);
                setCurrentSelectedServiceForDisplay(selectedService?.value);
                history.push(SINGLE_ESTIMATE_ROUTE);
            });
    };
    /**
     * Get Vision Estimates for the selected service and member details provided by the user in the form
     * @returns {Promise} - Promise object represents the vision estimate response
     * @throws {Error} - Error object if there is an error in the getVisionPrice function
     */
    const getVisionEstimates = async () => {
        try {
            const res = await getVisionPrice(formValues, memberDetails, getIncrementedCorId());
            setVisionEstimateResponse(res);
            setCurrentSelectedServiceForDisplay(selectedService?.value);
            history.push(SINGLE_ESTIMATE_ROUTE);
            setLoading(false);
        } catch (error) {
            setVisionEstimateResponse(null);
            setCurrentSelectedServiceForDisplay(selectedService?.value);
            history.push(SINGLE_ESTIMATE_ROUTE);
        }
    };

    const getBenefitsResponseValue = async (planBenefitsPromise) => {
        let planBenefitsFulfilled = {};

        const planBenefitsResponse = await Promise.allSettled([planBenefitsPromise]);
        if (planBenefitsResponse?.[0]?.status === 'fulfilled') {
            planBenefitsFulfilled = planBenefitsResponse?.[0]?.value;
            setPlanBenefits(planBenefitsFulfilled);
        }

        return planBenefitsFulfilled;
    };

    const renderProviderSearchSnackbars = () => {
        if (providerSearchFailureSnackBar) {
            return (
                <Snackbar
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    open={providerSearchFailureSnackBar}
                    autoHideDuration={6000}
                    onClose={handleProviderSearchFailureSnackBarClose}
                >
                    <Alert
                        onClose={handleProviderSearchFailureSnackBarClose}
                        severity="error"
                        sx={{ width: '100%' }}
                    >
                        Failed to retrieve requested providers
                    </Alert>
                </Snackbar>
            );
        }
    };

    const LocationSearchErrorPopup = (props) => {
        return (
            <Paper elevation={1} sx={{ width: '100%' }}>
                <LocationSearchErrorCard emptyLocation={emptyLocation} classes={classes} />
                <Paper elevation={0} sx={{ width: '100%' }} {...props} />
            </Paper>
        );
    };

    const renderServiceInputAutoCompleteComponent = () => {
        return (
            <ServiceSearchInput
                setNoCoverageDisclaimer={setNoCoverageDisclaimer}
                setInnNoNetworkDisclaimer={setInnNoNetworkDisclaimer}
                openLocationPopper={openLocationPopper}
                message={message}
                setMessage={setMessage}
                customPopper={customPopper}
                setCustomPopper={setCustomPopper}
                setServiceFocus={setServiceFocus}
                service={service}
                locationInputRef={locationInputRef}
                setServiceTypeDisclaimer={setServiceTypeDisclaimer}
                setDisableProviderDropdown={setDisableProviderDropdown}
                setDentalPolicyDisclaimer={setDentalPolicyDisclaimer}
            />
        );
    };

    const renderLocationServiceAutoComplete = () => {
        return (
            <Paper
                style={{
                    borderRadius: '32px',
                    padding: '5px',
                    paddingLeft: '21px',
                }}
                variant="outlined"
                data-testid="location-paper"
            >
                <Autocomplete
                    id="auto-complete-locationOfService"
                    size="small"
                    open={openLocatonPopper}
                    onOpen={openLocationPopper}
                    onClose={closeLocationPopper}
                    onBlur={() => {
                        setEmptyLocation(false);
                        closeLocationPopper();
                    }}
                    clearOnBlur={false}
                    PaperComponent={LocationSearchErrorPopup}
                    variant="standard"
                    label="Location"
                    data-testid="locationOfService"
                    disablePortal
                    onInputChange={(event, newValue) => {
                        debouncedLocationChange(newValue);
                    }}
                    onChange={(event, newValue) => {
                        handleChangeCustom(newValue, 'locationOfService');
                    }}
                    options={locationOptions}
                    value={locationOfService.value}
                    key={locationOfService.value}
                    getOptionLabel={(option) => (option?.place_name ? option.place_name : '')}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText={'No options in selected location'}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Street Address, City & State, Zip Code"
                            InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {locationOfService?.value ? (
                                            <IconButton
                                                sx={{
                                                    position: 'absolute',
                                                    '&:focus': {
                                                        outline: '#196ecf auto 1px',
                                                    },
                                                }}
                                                aria-label="clearLocation"
                                                onClick={() => {
                                                    handleChangeCustom(null, 'locationOfService');
                                                    setLocationOptions([]);
                                                    setLocationFocus(true);
                                                }}
                                                className="close-button"
                                            >
                                                <CloseIcon data-testid="clearLocation" />
                                            </IconButton>
                                        ) : (
                                            ''
                                        )}
                                    </InputAdornment>
                                ),
                            }}
                            inputRef={locationInputRef}
                            variant="standard"
                            focused={true}
                            sx={{
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#6F6F6F',
                                    size: '14px',
                                    fontWeight: 700,
                                },
                            }}
                        />
                    )}
                />
            </Paper>
        );
    };

    const renderRadiusAutoComplete = () => {
        return (
            <Paper
                style={{
                    borderRadius: '32px',
                    padding: '3px',
                    paddingLeft: '10px',
                }}
                variant="outlined"
            >
                <Autocomplete
                    id="auto-complete-setRadius"
                    disableClearable
                    sx={{
                        maxWidth: '100%',
                    }}
                    size={'small'}
                    variant="standard"
                    defaultValue="20mi"
                    disablePortal
                    data-testid="setRadius"
                    onFocus={() => {
                        setProviderSearchOpen(false);
                        setNavigateWithKey(false);
                    }}
                    value={radius.value}
                    key={radius.value}
                    onChange={(event, newValue) => {
                        handleChangeCustom(newValue, 'radius');
                    }}
                    clearOnBlur={false}
                    isOptionEqualToValue={(option, value) => option.label === value.label}
                    renderOption={(props, option, state) => (
                        <li {...props}>
                            <Box mr={2} sx={{ width: 10 }}>
                                {state.selected && <CheckIcon />}
                            </Box>
                            {option.label}
                        </li>
                    )}
                    options={radiusOptions}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            focused={true}
                            label="Distance"
                            data-testid="Distance"
                            InputLabelProps={{
                                style: {
                                    color: '#6F6F6F',
                                    size: '14px',
                                    fontWeight: 700,
                                },
                            }}
                            sx={{
                                marginTop: '10px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                    color: '#333333',
                                    fontWeight: 700,
                                },
                            }}
                        />
                    )}
                />
            </Paper>
        );
    };

    return (
        <Grid container>
            <Grid item xs={12}>
                {(innNoNetworkDisclaimerOpen ||
                    noCoverageDisclaimerOpen ||
                    serviceTypeDisclaimer ||
                    dentalPolicyDisclaimer) && (
                    <ServiceDisclaimerBanner
                        isIndemnity={isIndemnity}
                        noCoverageDisclaimerOpen={noCoverageDisclaimerOpen}
                        setProvider={setProvider}
                        handleSubmit={handleSubmit}
                        setInnNoNetworkDisclaimer={setInnNoNetworkDisclaimer}
                        setNoCoverageDisclaimer={setNoCoverageDisclaimer}
                        service={selectedService}
                        setServiceTypeDisclaimer={setServiceTypeDisclaimer}
                        serviceTypeDisclaimer={serviceTypeDisclaimer}
                        setDentalPolicyDisclaimer={setDentalPolicyDisclaimer}
                        dentalPolicyDisclaimer={dentalPolicyDisclaimer}
                    ></ServiceDisclaimerBanner>
                )}
            </Grid>
            <Grid item xs={12}>
                <Grid
                    container
                    wrap={isDesktopView ? 'nowrap' : 'wrap'}
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ padding: '10px' }}
                    ref={ref}
                >
                    {renderProviderSearchSnackbars()}
                    <Grid item xs={isDesktopView ? 3 : 12} style={{ cursor: 'pointer' }}>
                        {renderServiceInputAutoCompleteComponent()}
                    </Grid>
                    <Grid item xs={isDesktopView ? 3 : 12}>
                        {renderLocationServiceAutoComplete()}
                    </Grid>
                    <Grid item xs={isDesktopView ? 1 : 12} minWidth={'120px'}>
                        {renderRadiusAutoComplete()}
                    </Grid>
                    <Grid item xs={isDesktopView ? 3 : 12}>
                        <Paper
                            style={{
                                borderRadius: '32px',
                                padding: '5px',
                                paddingLeft: '21px',
                            }}
                            variant="outlined"
                        >
                            <Autocomplete
                                id="auto-complete-providerInput"
                                size="small"
                                variant="standard"
                                label="Doctor, specialist or facilty"
                                data-testid="providerInput"
                                disablePortal
                                value={selectedProvider.value}
                                key={selectedProvider.value}
                                onKeyDown={(e) => {
                                    if (e.key == 'Tab') {
                                        setNavigateWithKey(true);
                                    }
                                }}
                                onChange={(event, newValue) => {
                                    handleChangeCustom(newValue, 'selectedProvider');
                                }}
                                onClose={(event, reason) => {
                                    if (
                                        reason == 'blur' &&
                                        searchButtonDisabled &&
                                        navigateWithKey
                                    ) {
                                        setProviderSearchOpen(true); //keep the window open if button is disabled
                                    } else {
                                        setProviderSearchOpen(false); //close the window (normal behavior)
                                    }
                                    if (reason == 'selectOption') {
                                        setSearchButtonDisabled(false);
                                    }
                                }}
                                onOpen={() => {
                                    setProviderSearchOpen(true);
                                }}
                                open={providerSearchOpen}
                                clearOnBlur={false}
                                loading={providerSearchLoading}
                                loadingText={
                                    <div
                                        aria-live="polite" //announce loading state
                                    >
                                        Loading...
                                    </div>
                                }
                                options={providerSearchLoading ? [] : filteredProviderData}
                                groupBy={(option) => option.label}
                                getOptionLabel={(option) => option.unTranslatedText}
                                isOptionEqualToValue={(option, value) =>
                                    option.label === value.label
                                }
                                disabled={serviceTypeDisclaimer || disableProviderDropdown}
                                onInputChange={(event, newValue, reason) => {
                                    if (newValue == '') {
                                        debounceProviderSearch(newValue);
                                    } else if (
                                        newValue != selectedProvider.value &&
                                        reason != 'reset'
                                    ) {
                                        setSearchButtonDisabled(true);
                                        setProviderSearchLoading(true);
                                        debounceProviderSearch(newValue);
                                    }
                                }}
                                noOptionsText={
                                    <ProviderNoOptionsDropdown
                                        innNetworkGacFlow={innNetworkGacFlow}
                                        isIndemnity={isIndemnity}
                                        service={selectedService}
                                        navigateWithKey={navigateWithKey}
                                        setNavigateWithKey={setNavigateWithKey}
                                        setProvider={setProvider}
                                        setProviderSearchOpen={setProviderSearchOpen}
                                        handleSubmit={handleSubmit}
                                        umrIndemnity={umrIndemnity}
                                    ></ProviderNoOptionsDropdown>
                                }
                                renderInput={(params) => (
                                    <TextField
                                        placeholder="(this is optional)"
                                        variant="standard"
                                        focused={true}
                                        inputRef={providerInputRef}
                                        sx={{
                                            '& .MuiInputLabel-root.Mui-focused': {
                                                color: '#6F6F6F',
                                                size: '14px',
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                            },
                                            '.MuiInputBase-input ': {
                                                cursor: 'pointer',
                                            },
                                        }}
                                        {...params}
                                        label="Doctor, specialist or facility"
                                        InputProps={{
                                            ...params.InputProps,
                                            style: {
                                                color: '#333333',
                                                size: '14px',
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                            },
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {selectedProvider.value ? (
                                                        <IconButton
                                                            sx={{
                                                                position: 'absolute',
                                                                '&:focus': {
                                                                    outline: '#196ecf auto 1px',
                                                                },
                                                            }}
                                                            aria-label="Clear providerInput"
                                                            onClick={() => {
                                                                handleChangeCustom(
                                                                    null,
                                                                    'selectedProvider'
                                                                );
                                                                setSearchButtonDisabled(true);
                                                                setProviderSearchLoading(true);
                                                                setProviderData([]);
                                                                fetchProviderData();
                                                            }}
                                                        >
                                                            <CloseIcon
                                                                sx={{
                                                                    '&:focus': {
                                                                        outline: '#196ecf auto 1px',
                                                                    },
                                                                }}
                                                                data-testid="clearProvider"
                                                            />
                                                        </IconButton>
                                                    ) : (
                                                        ''
                                                    )}
                                                </InputAdornment>
                                            ),
                                            disableUnderline: true,
                                        }}
                                        options={filteredProviderData}
                                    />
                                )}
                            />
                        </Paper>
                    </Grid>
                    <Grid item xs={isDesktopView ? true : 12}>
                        <Button
                            onClick={(e) => {
                                if (searchButtonDisabled) {
                                    return;
                                }
                                e.preventDefault();
                                if (searchButtonDisabled && selectedService.value == null) {
                                    setCustomPopper(true);
                                } else if (
                                    searchButtonDisabled &&
                                    locationOfService.value == null
                                ) {
                                    setOpenLocationPopper(true);
                                } else {
                                    handleSubmit();
                                }
                            }}
                            onFocus={() => {
                                setProviderSearchOpen(false);
                                setNavigateWithKey(false);
                            }}
                            disabledBorder={false}
                            loading={providerSearchLoading}
                            data-testid="searchPageSubmit-button"
                            style={{ width: '100%' }}
                            buttonType={searchButtonDisabled ? 'disabled' : 'primary-one'}
                            aria-disabled={searchButtonDisabled}
                        >
                            Search
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
            {/* {renderProviderSearchErrorSnackbar()} */}
        </Grid>
    );
}
